import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, CornerDownLeft, X } from 'lucide-react';
import { CAMERA_MESSAGES, normalizeCameraCode, webCameraAvailable } from './cameraScan';
import CameraScanSheet from './CameraScanSheet';
import NumberPadSheet, { needsPagePad, padKey } from '../../components/NumberPadSheet';

const SEARCH_URL = '/api/v1/inventory-transfers/search';
const MOVE_URL = '/api/v1/inventory-transfers/moves';
const MOVE_TIMEOUT_MS = 30000;
const VERIFY_POLLS = 16;
const VERIFY_INTERVAL_MS = 5000;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
export const newRequestId = () => (window.crypto?.randomUUID
  ? window.crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

const UNKNOWN_REASONS = {
  in_progress: 'Eadin is still waiting on ShipHero.',
  shiphero_unavailable: 'ShipHero can\'t be reached to check.',
  other_changes: 'ShipHero shows other stock changes at these locations, and none tied to this transfer, so it can\'t prove either way.',
  not_in_log: 'ShipHero\'s change log doesn\'t show this transfer. That doesn\'t prove it didn\'t run, because the log can lag or miss entries.',
  no_shiphero_ids: 'One of the locations has no ShipHero id to check.',
  verify_failed: 'Eadin couldn\'t run the check.',
};

const EMPTY = { from: null, item: null, qty: '', to: null };

export { needsPagePad, padKey };

export const holdingOf = (location, sku) => (location?.stock || []).find((row) => row.sku === sku)?.quantity || 0;

export const readyToMove = ({ from, item, qty, to }) => Boolean(
  from && item && to && from.location_code !== to.location_code && Number.isInteger(Number(qty)) && Number(qty) > 0,
);

// Applies one resolved search to the transfer slots. Never moves stock.
export const applyResult = (slots, result) => {
  if (result.kind === 'location') {
    const location = { ...result.location, stock: result.stock };
    if (!slots.from && slots.item && !holdingOf(location, slots.item.sku)) {
      // A bin holding none of the item can't be the source, so it can only be the destination.
      if (slots.to) return { slots, notice: `${location.location_code} holds no ${slots.item.sku}.`, warn: true, resolved: true };
      return {
        slots: { ...slots, to: location },
        notice: `${location.location_code} holds no ${slots.item.sku}, so it's the To location. Tap or scan the From location.`,
        keepCandidates: true,
        resolved: true,
      };
    }
    if (!slots.from) {
      // A location holding a single SKU has only one possible item, so pick it.
      const only = !slots.item && location.stock.length === 1 ? location.stock[0] : null;
      const item = only ? { sku: only.sku, name: only.name } : slots.item;
      return { slots: { ...slots, from: location, item }, resolved: true };
    }
    if (slots.from.location_code === location.location_code) {
      return { slots, notice: `${location.location_code} is already the From location.`, resolved: true };
    }
    return { slots: { ...slots, to: location }, resolved: true };
  }
  if (result.kind === 'product') {
    const next = { ...slots, item: result.product };
    if (slots.from) {
      return holdingOf(slots.from, result.product.sku)
        ? { slots: next, resolved: true }
        : { slots: next, notice: `${slots.from.location_code} holds no ${result.product.sku}.`, warn: true, resolved: true };
    }
    if (!result.stock.length) return { slots: next, notice: `No location holds ${result.product.sku}.`, warn: true, resolved: true };
    if (result.stock.length === 1) {
      // An item in a single bin has only one possible source, so pick it.
      const [row] = result.stock;
      const stock = [{ sku: result.product.sku, name: result.product.name, quantity: row.quantity }];
      return { slots: { ...next, from: { location_code: row.location_code, stock } }, resolved: true };
    }
    return {
      slots: next,
      resolved: true,
      candidates: {
        title: `${result.product.sku} is in ${result.stock.length} location${result.stock.length === 1 ? '' : 's'}. Tap the one it's moving from:`,
        items: result.stock.map((row) => ({ kind: 'Location', key: row.location_code, label: row.location_code, sub: `${row.quantity} available` })),
      },
    };
  }
  if (result.kind === 'candidates') {
    const items = [
      ...result.locations.map((row) => ({ kind: 'Location', key: row.location_code, label: row.location_code, sub: row.physical_label && row.physical_label !== row.location_code ? row.physical_label : '' })),
      ...result.products.map((row) => ({ kind: 'SKU', key: row.sku, label: row.sku, sub: row.name || '' })),
    ];
    const title = result.reason === 'duplicate_barcode'
      ? `Barcode ${result.query} is on ${result.products.length} products. Pick one:`
      : `${items.length} match${items.length === 1 ? '' : 'es'} for "${result.query}". Tap one:`;
    return { slots, candidates: { title, items }, resolved: false };
  }
  return { slots, notice: `Nothing matches "${result.query}". Nothing changed.`, warn: true, resolved: false };
};

// Applies a search from the To box: only a location can fill it.
export const applyToResult = (slots, result) => {
  if (result.kind === 'location') {
    const location = { ...result.location, stock: result.stock };
    if (slots.from?.location_code === location.location_code) {
      return { slots, notice: `${location.location_code} is already the From location.`, warn: true, resolved: true };
    }
    return { slots: { ...slots, to: location }, resolved: true };
  }
  if (result.kind === 'product') {
    return { slots, notice: `${result.product.sku} is an item, not a location. Scan or type the To location.`, warn: true, resolved: true };
  }
  if (result.kind === 'candidates' && result.locations.length) {
    const items = result.locations.map((row) => ({ kind: 'Location', key: row.location_code, label: row.location_code, sub: row.physical_label && row.physical_label !== row.location_code ? row.physical_label : '' }));
    return { slots, candidates: { title: `${items.length} location${items.length === 1 ? '' : 's'} match "${result.query}". Tap the To location:`, items, target: 'to' }, resolved: false };
  }
  return { slots, notice: `No location matches "${result.query}". Nothing changed.`, warn: true, resolved: false };
};

// Plain-language reason Move is off, so a phone user knows which row still needs them.
export const moveBlocker = ({ from, item, qty, to }) => {
  if (!item) return from ? `Tap the item to move from ${from.location_code} above.` : 'Scan or type the item to move.';
  if (!from) return 'Tap or scan the From location.';
  if (!(Number.isInteger(Number(qty)) && Number(qty) > 0)) return 'Enter a quantity.';
  if (!to) return 'Scan or type the To location.';
  if (from.location_code === to.location_code) return 'From and To are the same location.';
  return null;
};

const errorMessage = (err) => err?.response?.data?.error || err?.message || 'Request failed';

// An empty slot is a tap target for the search box: iOS number pads have no Enter key,
// so tapping To is how a phone user gets back to the box to scan or type the destination.
const Slot = ({ label, value, onClear, onFill, children }) => (
  <div className="flex min-h-[44px] items-center gap-3 border-b border-gray-100 py-1 last:border-b-0">
    <span className="w-14 shrink-0 text-xs font-medium uppercase tracking-wide text-gray-500">{label}</span>
    {!value && onFill
      ? <button type="button" onClick={onFill} aria-label={`Scan or type ${label}`} className="min-h-[44px] min-w-0 flex-1 text-left">{children}</button>
      : <div className="min-w-0 flex-1">{children}</div>}
    {value && onClear && (
      <button type="button" aria-label={`Clear ${label}`} onClick={onClear} className="flex h-11 w-11 items-center justify-center text-gray-400 hover:text-gray-700">
        <X size={18} />
      </button>
    )}
  </div>
);

const InventoryTransfers = ({ canEdit = false }) => {
  const [slots, setSlots] = useState(EMPTY);
  const [query, setQuery] = useState('');
  const [toQuery, setToQuery] = useState('');
  const toRef = useRef(null);
  const cameraTargetRef = useRef('search');
  const [candidates, setCandidates] = useState(null);
  const [notice, setNotice] = useState(null);
  const [searching, setSearching] = useState(false);
  const [unresolved, setUnresolved] = useState(false);
  const [step, setStep] = useState('edit');
  const [result, setResult] = useState(null);
  const [failure, setFailure] = useState(null);
  const [unknown, setUnknown] = useState(null);
  const [attemptId, setAttemptId] = useState(null);
  const searchRef = useRef(null);
  const qtyRef = useRef(null);
  const [pagePad] = useState(needsPagePad);
  const [padOpen, setPadOpen] = useState(false);
  const nextFocusRef = useRef('search');
  const [cameraOpen, setCameraOpen] = useState(false);
  const cameraAvailable = webCameraAvailable();
  const slotsRef = useRef(EMPTY);
  const stepRef = useRef('edit');
  const queueRef = useRef(Promise.resolve());
  const pendingRef = useRef(0);
  useEffect(() => { slotsRef.current = slots; }, [slots]);
  useEffect(() => { stepRef.current = step; }, [step]);

  const focusSearch = useCallback(() => {
    requestAnimationFrame(() => {
      if (document.activeElement !== searchRef.current) searchRef.current?.focus();
    });
  }, []);

  useEffect(() => { focusSearch(); }, [focusSearch]);


  // Called from a tap, so focus synchronously: iOS only opens the keyboard inside the gesture.
  const closePad = useCallback(() => setPadOpen(false), []);

  const tapToSearch = () => { if (!busy) searchRef.current?.focus(); };

  // After a source is picked the next entry is the quantity; otherwise the next scan.
  const focusNext = useCallback(() => {
    if (nextFocusRef.current === 'qty') {
      nextFocusRef.current = 'search';
      requestAnimationFrame(() => qtyRef.current?.focus());
    } else {
      focusSearch();
    }
  }, [focusSearch]);

  const clearSearch = () => {
    setCandidates(null); setNotice(null); setUnresolved(false);
    focusSearch();
  };

  const reset = () => {
    setSlots(EMPTY); setCandidates(null); setNotice(null); setUnresolved(false);
    setResult(null); setFailure(null); setUnknown(null); setAttemptId(null); setStep('edit');
    focusSearch();
  };

  // Scans are applied one at a time, in order, each to the latest slots, so a
  // fast second scan can't overwrite the first.
  const runOne = async (q, target) => {
    if (stepRef.current === 'saving' || stepRef.current === 'checking') return;
    if (stepRef.current !== 'edit') {
      slotsRef.current = EMPTY; stepRef.current = 'edit';
      setSlots(EMPTY); setResult(null); setFailure(null); setUnknown(null); setAttemptId(null); setStep('edit');
    }
    setUnresolved(true); setNotice(null);
    try {
      const { data } = await axios.get(SEARCH_URL, { params: { q } });
      const applied = target === 'to' ? applyToResult(slotsRef.current, data) : applyResult(slotsRef.current, data);
      slotsRef.current = applied.slots;
      setSlots(applied.slots);
      setCandidates((shown) => (applied.keepCandidates ? shown : applied.candidates || null));
      setNotice(applied.notice ? { text: applied.notice, warn: Boolean(applied.warn) } : null);
      setUnresolved(!applied.resolved);
      const next = applied.slots;
      nextFocusRef.current = next.from && next.item && next.qty === '' && !applied.candidates ? 'qty' : 'search';
    } catch (err) {
      setCandidates(null);
      setNotice({ text: `Search failed: ${errorMessage(err)}`, warn: true });
    }
  };

  const runSearch = (raw, target = 'search') => {
    const q = String(raw || '').trim();
    if (!q || step === 'saving' || step === 'checking') return;
    pendingRef.current += 1;
    setSearching(true);
    queueRef.current = queueRef.current.then(() => runOne(q, target)).finally(() => {
      pendingRef.current -= 1;
      if (!pendingRef.current) setSearching(false);
      focusNext();
    });
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const value = query;
    setQuery('');
    runSearch(value);
  };

  const onToSubmit = (event) => {
    event.preventDefault();
    const value = toQuery;
    setToQuery('');
    runSearch(value, 'to');
  };

  const onSearchKeyDown = (event) => {
    if (event.key === 'Tab' && query.trim()) {
      event.preventDefault();
      onSubmit(event);
    }
  };

  const pickSku = (row) => {
    setSlots((current) => ({ ...current, item: { sku: row.sku, name: row.name } }));
    setCandidates(null); setNotice(null); setUnresolved(false);
    nextFocusRef.current = slotsRef.current.qty === '' ? 'qty' : 'search';
    focusNext();
  };

  // The camera is a backup to the Socket scanner: it only fills the same search.
  const openCamera = (target = 'search') => {
    if (!cameraAvailable || busy) return;
    cameraTargetRef.current = target;
    setNotice(null);
    setCameraOpen(true);
  };

  const onCameraRead = (read) => {
    setCameraOpen(false);
    const code = normalizeCameraCode(read);
    if (code) { setQuery(''); runSearch(code, cameraTargetRef.current); }
  };

  const onCameraError = (code) => {
    setCameraOpen(false);
    setNotice({ text: CAMERA_MESSAGES[code] || CAMERA_MESSAGES.unavailable, warn: true });
    focusSearch();
  };

  const applyVerified = (data) => {
    if (data.outcome === 'moved') {
      setResult({ move: data.move, ...data.result }); setStep('done');
    } else if (data.outcome === 'not_moved') {
      setFailure({
        text: data.message || 'The move stopped before ShipHero was asked.',
      });
      setAttemptId(null); setStep('error');
    } else {
      setUnknown(data); setStep('unknown');
    }
  };

  const checkOutcome = async (requestId) => {
    setStep('checking');
    for (let poll = 0; poll < VERIFY_POLLS; poll += 1) {
      let data;
      try {
        ({ data } = await axios.post(`${MOVE_URL}/${requestId}/verify`, {}, { timeout: 20000 }));
      } catch (err) {
        data = { outcome: 'unknown', reason: 'verify_failed', error: errorMessage(err) };
      }
      if (data.outcome !== 'in_progress') { applyVerified(data); return; }
      await sleep(VERIFY_INTERVAL_MS);
    }
    applyVerified({ outcome: 'unknown', reason: 'in_progress' });
  };

  const searchBlocked = searching || unresolved || query.trim() !== '' || toQuery.trim() !== '';

  const move = async () => {
    if (!canEdit || !readyToMove(slots) || searchBlocked || step !== 'edit') return;
    const requestId = newRequestId();
    setAttemptId(requestId); setStep('saving'); setFailure(null); setUnknown(null);
    try {
      const { data } = await axios.post(MOVE_URL, {
        sku: slots.item.sku, from: slots.from.location_code, to: slots.to.location_code, quantity: Number(slots.qty), requestId,
      }, { timeout: MOVE_TIMEOUT_MS });
      setResult(data); setStep('done');
    } catch (err) {
      const data = err?.response?.data || {};
      if (data.outcome === 'not_moved') {
        setFailure({ text: errorMessage(err) }); setAttemptId(null); setStep('error');
      } else if (data.outcome === 'blocked' && data.requestId) {
        setNotice({ text: `${data.error}. Checking it first.`, warn: true });
        setAttemptId(data.requestId);
        await checkOutcome(data.requestId);
      } else {
        await checkOutcome(requestId);
      }
    } finally {
      focusSearch();
    }
  };

  const { from, item, to } = slots;
  const editing = step === 'edit';
  const busy = step === 'saving' || step === 'checking';
  const ready = readyToMove(slots);
  const searchPrompt = item && from && slots.qty && !to
    ? 'Scan or type the To location'
    : item && !from
      ? 'Scan the From location, or tap one below'
      : 'Scan or type location, SKU, barcode or product';
  const fromHolds = from && item ? holdingOf(from, item.sku) : null;
  const toHolds = to && item ? holdingOf(to, item.sku) : null;
  const moveLabel = step === 'saving' ? 'Moving…' : `Move${ready ? ` ${Number(slots.qty)} × ${item.sku}` : ''}`;

  return (
    <div className="mx-auto w-full max-w-xl p-3">
      <h1 className="mb-2 text-lg font-semibold text-gray-900">Inventory Transfer</h1>
      <form role="search" onSubmit={onSubmit} className="flex gap-2" autoComplete="off">
        <input
          ref={searchRef}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={onSearchKeyDown}
          type="text"
          name="q"
          aria-label="Scan or type a location, SKU, barcode or product"
          placeholder={searchPrompt}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          enterKeyHint="search"
          inputMode="text"
          onFocus={() => setPadOpen(false)}
          disabled={busy}
          className="h-11 min-w-0 flex-1 rounded-lg border-2 border-gray-800 px-3 text-base outline-none focus:border-gray-900"
        />
        {cameraAvailable && (
          <button type="button" aria-label="Scan with camera" title="Scan with camera" onClick={() => openCamera('search')} disabled={busy}
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-800 disabled:opacity-50">
            <Camera size={20} />
          </button>
        )}
        <button type="submit" aria-label="Search" title="Search" disabled={busy || searching}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white disabled:opacity-50">
          <CornerDownLeft size={20} />
        </button>
      </form>

      {(notice || candidates) && (
        <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
          <div className="flex items-center bg-gray-50">
            <div role="status" className={`flex-1 px-3 py-1.5 text-sm ${notice?.warn ? 'text-amber-800' : 'text-gray-600'}`}>
              {notice?.text || <span className="text-xs font-medium uppercase tracking-wide text-gray-500">{candidates.title}</span>}
            </div>
            <button type="button" aria-label="Clear search" title="Clear search" onClick={clearSearch}
              className="flex h-11 w-11 shrink-0 items-center justify-center text-gray-400 hover:text-gray-700">
              <X size={18} />
            </button>
          </div>
          {candidates && notice && (
            <div className="border-t border-gray-100 px-3 py-1.5 text-xs font-medium uppercase tracking-wide text-gray-500">{candidates.title}</div>
          )}
          {candidates && (
            <div role="listbox" aria-label="Matches">
              {candidates.items.map((row) => (
                <button key={`${row.kind}:${row.key}`} type="button" role="option" aria-selected="false"
                  onClick={() => { setCandidates(null); runSearch(row.key, candidates.target); }}
                  className="flex min-h-[44px] w-full items-center gap-2 border-t border-gray-100 px-3 text-left hover:bg-gray-50">
                  <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{row.kind}</span>
                  <span className="shrink-0 whitespace-nowrap text-gray-900">{row.label}</span>
                  <span className="ml-auto min-w-0 truncate text-sm text-gray-500">{row.sub}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="mt-2 rounded-lg border border-gray-200 px-3">
        <Slot label="Item" value={item} onFill={editing ? tapToSearch : null} onClear={editing ? () => { setSlots((s) => ({ ...s, item: null })); focusSearch(); } : null}>
          {item ? <span><b className="text-gray-900">{item.sku}</b> <span className="text-sm text-gray-500">{item.name}</span></span> : <span className="text-gray-400">scan a SKU or barcode</span>}
        </Slot>
        {editing && from && !item && (
          <div className="pb-2">
            <div className="px-1 pb-1 text-xs font-medium uppercase tracking-wide text-gray-500">In {from.location_code}, tap the item to move:</div>
            {from.stock.length ? from.stock.map((row) => (
              <button key={row.sku} type="button" onClick={() => pickSku(row)}
                className="flex min-h-[44px] w-full items-center gap-2 border-t border-gray-100 px-1 text-left hover:bg-gray-50">
                <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">SKU</span>
                <span className="min-w-0 truncate whitespace-nowrap">{row.sku}</span>
                <span className="ml-auto shrink-0 whitespace-nowrap text-sm text-gray-500">{row.quantity} available</span>
              </button>
            )) : <div className="py-2 text-sm text-gray-500">Empty location</div>}
          </div>
        )}
        <Slot label="From" value={from} onFill={editing ? tapToSearch : null} onClear={editing ? () => { setSlots((s) => ({ ...s, from: null })); focusSearch(); } : null}>
          {from
            ? <span className="text-gray-900">{from.location_code} {fromHolds !== null && editing && <span className="text-sm text-gray-500">{fromHolds} available</span>}</span>
            : <span className="text-gray-400">{item ? 'tap a location above, or scan one' : 'scan a location'}</span>}
        </Slot>
        <Slot label="Qty">
          <div className="flex items-center gap-2">
            <input
              ref={qtyRef}
              aria-label="Quantity to move"
              value={slots.qty}
              onChange={(event) => setSlots((s) => ({ ...s, qty: event.target.value.replace(/\D/g, '') }))}
              onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); focusSearch(); } }}
              type="text"
              inputMode={pagePad ? 'none' : 'numeric'}
              pattern="[0-9]*"
              enterKeyHint="next"
              onClick={() => { if (pagePad) setPadOpen(true); }}
              disabled={!editing}
              className="h-11 w-32 rounded-lg border border-gray-300 px-3 text-base"
            />
            {fromHolds > 0 && editing && (
              <button type="button" onClick={() => { setSlots((s) => ({ ...s, qty: String(fromHolds) })); focusSearch(); }}
                className="h-11 rounded-lg border border-gray-300 px-3 text-sm text-gray-800">All {fromHolds}</button>
            )}
          </div>
          {pagePad && padOpen && editing && (
            <NumberPadSheet
              label="Qty"
              value={slots.qty}
              anchorRef={qtyRef}
              onChange={(qty) => setSlots((s) => ({ ...s, qty }))}
              onClose={closePad}
              onDone={() => { setPadOpen(false); focusSearch(); }}
            />
          )}
        </Slot>
        <Slot label="To" value={to} onClear={editing ? () => { setSlots((s) => ({ ...s, to: null })); requestAnimationFrame(() => toRef.current?.focus()); } : null}>
          {to && <span className="text-gray-900">{to.location_code} {item && editing && <span className="text-sm text-gray-500">now holds {toHolds} {item.sku}</span>}</span>}
          {!to && editing && (
            <form onSubmit={onToSubmit} className="flex gap-2 py-1" autoComplete="off">
              <input
                ref={toRef}
                value={toQuery}
                onChange={(event) => setToQuery(event.target.value)}
                type="text"
                aria-label="To location"
                placeholder="Scan or type a location"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                enterKeyHint="go"
                inputMode="text"
                onFocus={() => setPadOpen(false)}
                disabled={busy}
                className="h-11 min-w-0 flex-1 rounded-lg border border-gray-300 px-3 text-base outline-none focus:border-gray-900"
              />
              {cameraAvailable && (
                <button type="button" aria-label="Scan To with camera" title="Scan To with camera" onClick={() => openCamera('to')} disabled={busy}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-gray-300 text-gray-800 disabled:opacity-50">
                  <Camera size={20} />
                </button>
              )}
              <button type="submit" aria-label="Set To location" title="Set To location" disabled={busy || searching}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-gray-900 text-white disabled:opacity-50">
                <CornerDownLeft size={20} />
              </button>
            </form>
          )}
        </Slot>
      </div>

      {(editing || step === 'saving') && (
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <button type="button" onClick={move} disabled={!canEdit || !ready || searchBlocked || !editing}
            className="h-11 rounded-lg bg-gray-900 px-4 text-white disabled:bg-gray-400">
            {moveLabel}
          </button>
          {ready && editing && <span className="text-sm text-gray-600">{from.location_code} → {to.location_code}</span>}
          <button type="button" onClick={reset} disabled={busy} className="h-11 rounded-lg bg-gray-100 px-4 text-gray-800">Clear</button>
          {editing && !ready && (from || item || to || slots.qty) && (
            <span className="w-full text-sm text-amber-800">{moveBlocker(slots)}</span>
          )}
          {editing && ready && searchBlocked && !searching && (
            <span className="text-sm text-gray-500">Finish or clear the search to move.</span>
          )}
        </div>
      )}
      {!canEdit && <div className="mt-2 text-sm text-gray-500">View only: your role can search but not move stock.</div>}

      {step === 'checking' && (
        <div role="status" className="mt-2 rounded-lg bg-gray-50 px-3 py-2 text-sm text-gray-700">
          <b>No answer yet.</b> Checking with ShipHero whether it moved. Move stays off until Eadin knows.
        </div>
      )}

      {step === 'error' && failure && (
        <div className="mt-2">
          <div role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800">
            <b>Not moved.</b> {failure.text}<br />Nothing changed in ShipHero or Eadin.
          </div>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={() => { setStep('edit'); focusSearch(); }} className="h-11 rounded-lg bg-gray-100 px-4">Back</button>
            <button type="button" onClick={reset} className="h-11 rounded-lg bg-gray-100 px-4">Start over</button>
          </div>
        </div>
      )}

      {step === 'unknown' && unknown && (
        <div className="mt-2">
          <div role="alert" className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">
            {unknown.outcome === 'moved_not_recorded' ? (
              <>
                <b>ShipHero moved it, but Eadin didn't record it.</b> ShipHero's change log shows this transfer ({unknown.move.quantity} × {unknown.move.sku}, {unknown.move.from} → {unknown.move.to}). Don't move it again. Eadin's location numbers stay behind until the next ShipHero location sync.
              </>
            ) : (
              <>
                <b>Can't confirm whether this moved.</b> {UNKNOWN_REASONS[unknown.reason] || UNKNOWN_REASONS.verify_failed} Eadin blocks this same transfer until it's confirmed.
              </>
            )}
          </div>
          <div className="mt-2 flex gap-2">
            {unknown.outcome !== 'moved_not_recorded' && (
              <button type="button" onClick={() => checkOutcome(attemptId)} className="h-11 rounded-lg bg-gray-900 px-4 text-white">Check again</button>
            )}
            <button type="button" onClick={reset} className="h-11 rounded-lg bg-gray-100 px-4">Start over</button>
          </div>
        </div>
      )}

      {step === 'done' && result && (
        <div className="mt-2">
          <div role="status" className="rounded-lg bg-gray-100 px-3 py-2 text-sm text-gray-900">
            <b>Moved · confirmed.</b> {result.move.quantity} × {result.move.sku}, {result.move.from} → {result.move.to}.
            {' '}Now {result.source.code} has {result.source.quantity} and {result.destination.code} has {result.destination.quantity}.{' '}
            {result.verified === true && 'ShipHero and Eadin both updated, and ShipHero\'s read-back matched.'}
            {result.verified === false && 'ShipHero accepted the move, but its read-back didn\'t match the expected quantities, so Eadin saved ShipHero\'s numbers.'}
            {result.verified !== true && result.verified !== false && 'ShipHero accepted the move. Its read-back was unavailable, so Eadin saved the expected quantities.'}
          </div>
          <button type="button" onClick={reset} className="mt-2 h-11 rounded-lg bg-gray-900 px-4 text-white">Next transfer</button>
        </div>
      )}
      {cameraOpen && (
        <CameraScanSheet onRead={onCameraRead} onError={onCameraError} onClose={() => { setCameraOpen(false); focusSearch(); }} />
      )}
    </div>
  );
};

export default InventoryTransfers;
